import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import { Reveal } from "../components/Motion";
import SpecSection from "../components/SpecSection";
import Faq from "../components/Faq";
import { PRODUCTS } from "../lib/data";

export default function ProductPage() {
    const { slug } = useParams();
    const product = PRODUCTS.find((p) => p.slug === slug);
    if (!product) return <Navigate to="/" replace />;

    const schema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://thepuropack.com/" },
            { "@type": "ListItem", position: 2, name: "Products", item: "https://thepuropack.com/sustainable-packaging" },
            { "@type": "ListItem", position: 3, name: product.title, item: `https://thepuropack.com/products/${product.slug}` },
        ],
    };

    return (
        <div data-testid={`product-page-${product.slug}`}>
            <Seo title={product.title} description={product.summary} path={`/products/${product.slug}`} image={product.image} schema={schema} type="product" />
            <div className="max-w-[1600px] mx-auto px-6 lg:px-10">
                <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Products", to: "/sustainable-packaging" }, { label: product.title, to: `/products/${product.slug}` }]} />
            </div>

            <section className="max-w-[1600px] mx-auto px-6 lg:px-10 pt-12 pb-24 grid lg:grid-cols-12 gap-10 items-start">
                <div className="lg:col-span-7 order-2 lg:order-1">
                    <div className="font-mono text-xs uppercase tracking-widest text-clay mb-6">[ Chapter {product.chapter} · Foodservice ]</div>
                    <h1 className="font-serif text-6xl lg:text-8xl leading-[0.95] tracking-tight text-forest">{product.title}</h1>
                    <p className="mt-8 font-serif italic text-2xl lg:text-3xl text-forest max-w-xl">{product.tagline}</p>
                    <p className="mt-6 text-inkgreen/80 leading-relaxed max-w-2xl">{product.summary}</p>
                    <div className="mt-10 flex gap-4 flex-wrap">
                        <Link to="/contact" className="btn-pill btn-solid" data-testid="product-cta-enquire">Request Samples <ArrowUpRight className="w-4 h-4" /></Link>
                        {product.slug !== "pet-flat-lids" && <Link to="/custom-printing" className="btn-pill">Add Custom Print</Link>}
                    </div>
                </div>
                <div className="lg:col-span-5 order-1 lg:order-2">
                    <div className="image-frame aspect-[4/5]">
                        <img src={product.image} alt={`${product.title} — sustainable paper packaging by The Puro Pack`} />
                    </div>
                </div>
            </section>

            <section className="bg-sand border-y border-forest">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-24">
                    <div className="font-mono text-xs uppercase tracking-widest text-stone mb-6">[ At a Glance ]</div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-forest/40">
                        {product.highlights.map((h) => (
                            <div key={h.label} className="border-r border-b border-forest/40 p-8">
                                <div className="font-mono text-xs uppercase tracking-widest text-stone mb-4">{h.label}</div>
                                <div className="font-serif text-3xl text-forest leading-tight">{h.value}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {product.specs && <SpecSection specs={product.specs} title={`${product.title} sizes`} />}

            {product.faqs && (
                <div className="bg-bone border-t border-forest/20">
                    <Faq
                        faqs={product.faqs}
                        kicker="[ Product Questions ]"
                        heading={
                            <>
                                Everything buyers <em className="italic">ask</em> about {product.title.toLowerCase()}.
                            </>
                        }
                    />
                </div>
            )}

            <section className="max-w-[1600px] mx-auto px-6 lg:px-10 py-24">
                <Reveal>
                    <h2 className="font-serif text-4xl lg:text-6xl text-forest tracking-tight mb-10">Related products.</h2>
                </Reveal>
                <div className="grid md:grid-cols-3 gap-6">
                    {PRODUCTS.filter((p) => p.slug !== product.slug).slice(0, 3).map((p) => (
                        <Link key={p.slug} to={`/products/${p.slug}`} className="block group" data-testid={`related-${p.slug}`}>
                            <div className="image-frame aspect-[4/5]">
                                <img src={p.image} alt={p.title} loading="lazy" />
                            </div>
                            <div className="pt-4 flex justify-between items-start">
                                <div>
                                    <div className="font-mono text-xs uppercase tracking-widest text-clay">{p.chapter}</div>
                                    <div className="font-serif text-2xl text-forest">{p.title}</div>
                                </div>
                                <ArrowUpRight className="text-forest group-hover:text-clay" />
                            </div>
                        </Link>
                    ))}
                </div>
            </section>
        </div>
    );
}
